import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Spinner } from "@/components/common";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Send, MoreVertical, Ban, Flag } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

export default function Chat() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = () => api.get(`/conversations/${id}/messages`).then((r) => setState(r.data)).catch(() => toast.error("Chat not found"));
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [state?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    const val = text.trim();
    if (!val) return;
    setText("");
    setSending(true);
    try {
      const { data } = await api.post(`/conversations/${id}/messages`, { text: val });
      setState((s) => ({ ...s, messages: [...s.messages, data] }));
    } catch { toast.error("Message not sent"); setText(val); }
    finally { setSending(false); }
  };

  const block = async () => { await api.post(`/conversations/${id}/block`); toast("User blocked"); };
  const report = async () => { await api.post("/report", { target_type: "user", target_id: state.conversation.other?.user_id, reason: "Reported from chat" }); toast("Reported. Our team will review."); };

  if (!state) return <div><div className="h-16" /><Spinner /></div>;
  const other = state.conversation.other;
  const isGroup = state.conversation.group;
  const members = state.conversation.members || [];
  const listing = state.conversation.listing;

  return (
    <div className="flex h-screen flex-col bg-background">
      <header className="flex items-center gap-3 border-b border-gray-100 bg-white/90 px-3 py-3 backdrop-blur-xl">
        <button onClick={() => navigate(-1)} className="grid h-10 w-10 place-items-center rounded-full bg-secondary"><ArrowLeft className="h-5 w-5" /></button>
        {isGroup ? (
          <>
            <div className="flex -space-x-3">
              {members.slice(0, 3).map((m) => <img key={m.user_id} src={resolveImg(m.profile?.photo_url) || "https://images.unsplash.com/photo-1625241152315-4a698f74ceb7?w=200"} alt="" className="h-9 w-9 rounded-full border-2 border-white object-cover" />)}
            </div>
            <div className="flex-1">
              <h1 className="line-clamp-1 font-heading font-bold leading-tight text-ink">{state.conversation.title || "Tribe chat"}</h1>
              <p className="text-xs text-ink-soft">{members.length} members · group chat</p>
            </div>
          </>
        ) : (
          <>
            <img onClick={() => navigate(`/person/${other?.user_id}`)} src={resolveImg(other?.profile?.photo_url) || "https://images.unsplash.com/photo-1625241152315-4a698f74ceb7?w=200"} alt="" className="h-10 w-10 cursor-pointer rounded-full object-cover" />
            <div className="flex-1">
              <h1 className="font-heading font-bold leading-tight text-ink">{other?.profile?.first_name || other?.name}</h1>
              <p className="text-xs text-ink-soft">Phone numbers stay private</p>
            </div>
          </>
        )}
        {!isGroup && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild><button data-testid="chat-menu" className="grid h-10 w-10 place-items-center rounded-full bg-secondary"><MoreVertical className="h-5 w-5" /></button></DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={block}><Ban className="mr-2 h-4 w-4" /> Block</DropdownMenuItem>
            <DropdownMenuItem onClick={report}><Flag className="mr-2 h-4 w-4" /> Report</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        )}
      </header>

      {isGroup && (
        <div className="flex flex-col gap-1 border-b border-gray-100 bg-white px-4 py-2">
          {state.messages.length === 0 && <p className="text-center text-xs text-ink-soft">This is the start of your tribe group chat 👋</p>}
        </div>
      )}

      {listing && (
        <div onClick={() => navigate(`/listing/${listing.listing_id}`)} className="flex cursor-pointer items-center gap-3 border-b border-gray-100 bg-white px-4 py-2">
          <img src={resolveImg(listing.images?.[0])} alt="" className="h-10 w-10 rounded-xl object-cover" />
          <div className="flex-1"><p className="text-xs text-ink-soft">About listing</p><p className="line-clamp-1 text-sm font-semibold text-ink">{listing.title}</p></div>
        </div>
      )}

      <div className="flex-1 space-y-2 overflow-y-auto px-4 py-4">
        {state.messages.map((m) => {
          const mine = m.sender_id === user.user_id;
          const sender = isGroup && !mine ? members.find((x) => x.user_id === m.sender_id) : null;
          return (
            <div key={m.message_id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
              {sender && <span className="mb-0.5 ml-1 text-[10px] font-semibold text-ink-soft">{sender.profile?.first_name || sender.name}</span>}
              <div className={`max-w-[75%] rounded-3xl px-4 py-2.5 text-sm ${mine ? "rounded-br-lg bg-brand text-white" : "rounded-bl-lg bg-white text-ink shadow-soft"}`}>{m.text}</div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="flex items-center gap-2 border-t border-gray-100 bg-white px-3 py-3">
        <Input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="h-12 flex-1 rounded-full border-border bg-secondary px-4" />
        <button data-testid="chat-send" type="submit" disabled={sending} className="grid h-12 w-12 place-items-center rounded-full bg-brand text-white shadow-brand active:scale-90"><Send className="h-5 w-5" /></button>
      </form>
    </div>
  );
}
